import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { SelectOption, searchTextOf } from './search-select.model';

/**
 * The searchable select for the whole workspace — single or multiple, with a
 * second line and a badge per row so an option is recognisable before it is
 * chosen.
 *
 * WHY A WRAPPER, NOT RAW ng-select. ng-select is already a dependency and is
 * used by admissions-crm, security, system-admin and group-admin, so it is the
 * incumbent, and building a bespoke listbox would have made a third pattern
 * (hr and academics still use bare native selects). But used raw it does not
 * give consistency: every screen repeats its own `ng-option-tmp`, and the two
 * settings that actually matter — `appendTo="body"` and a search that also
 * matches the secondary line — get re-decided each time. Both are fixed here.
 *
 * `appendTo="body"` is not cosmetic. Screens wrap tables in
 * `.card { overflow: hidden }` and `.tbl-wrap { overflow-x: auto }`, which clip
 * an in-flow panel on the last rows, and no z-index fixes that. It is why the
 * Teacher Mapping table was still on a native `<select>` — the browser renders
 * those outside the document flow, so they were immune.
 *
 * Implements ControlValueAccessor, so `formControlName`, `[(ngModel)]` and
 * plain `[value]`/`(valueChange)` all work — the workspace uses all three.
 */
@Component({
    selector: 'app-search-select',
    standalone: true,
    imports: [CommonModule, FormsModule, NgSelectModule],
    templateUrl: './search-select.component.html',
    styleUrls: ['./search-select.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SearchSelectComponent),
        multi: true
    }]
})
export class SearchSelectComponent implements ControlValueAccessor, OnChanges {

    @Input() options: SelectOption[] = [];
    @Input() multiple = false;
    @Input() placeholder = 'Select…';
    @Input() notFoundText = 'No matches';
    @Input() disabled = false;
    @Input() clearable = true;

    /**
     * Accessible name for the control. One of these two is required wherever
     * there is no visible label: the focus target is ng-select's own inner
     * input, so a `for`/`aria-label` on anything outside this component never
     * reaches it.
     *
     * `ariaLabel` for toolbar controls, whose name lives in a sibling pill;
     * `labelForId` for form fields, which puts the id on that inner input so
     * an ordinary `<label for="…">` binds to it.
     */
    @Input() ariaLabel?: string;
    @Input() labelForId?: string;

    /**
     * The banana-in-a-box half of the plain `[value]`/`(valueChange)` usage.
     * Also the field ControlValueAccessor writes into, so a screen may bind it
     * directly or hand the component to a form — never both at once.
     */
    @Input() value: unknown = null;

    @Output() valueChange = new EventEmitter<unknown>();

    /**
     * Kept as a field rather than a getter: ng-select re-reads `[items]` by
     * reference, and a getter would hand it a fresh array on every change
     * detection pass, resetting the marked row mid-keyboard-navigation.
     */
    items: SelectOption[] = [];

    private onChange: (value: unknown) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    ngOnChanges(changes: SimpleChanges): void {
        // By reference is not enough. A caller that binds a getter —
        // `[options]="gradeOptions"` over `get gradeOptions() { return …map(…) }`
        // — hands a brand-new array on every change detection pass, so this
        // fires constantly and rebuilds `items` under ng-select, which resets
        // the marked row and makes the control impossible to change. Keeping
        // `items` a field does not save a caller from that; comparing the
        // contents does. The lists here are tens of rows, so it is cheaper than
        // the rebuild it avoids.
        if (!changes['options']) {
            return;
        }
        const next = this.options ?? [];
        if (!this.optionsDiffer(next)) {
            return;
        }
        // Against the raw previous input, not against `items`: `items` is the
        // sorted copy, so a list carrying flagged rows would compare unequal to
        // itself and the guard would never hold for exactly the lists that most
        // need it.
        this.lastOptions = [...next];
        // Flagged rows sort last but stay selectable — records already point
        // at them, and dropping them would make those records un-editable.
        this.items = [...next].sort((a, b) => Number(!!a.flagged) - Number(!!b.flagged));
    }

    /** The raw list as last received, so the comparison above is like for like. */
    private lastOptions: SelectOption[] = [];

    private optionsDiffer(next: SelectOption[]): boolean {
        if (next.length !== this.lastOptions.length) {
            return true;
        }
        return next.some((option, index) => {
            const previous = this.lastOptions[index];
            return !previous || !SearchSelectComponent.sameOption(previous, option);
        });
    }

    /**
     * Every field of the option, not a chosen few.
     *
     * <p>Listing them by name meant the list had to be kept in step with
     * SelectOption by hand, and it already was not: `disabled` and `flagNote`
     * were missing, so a parent that newly disabled an option left it
     * selectable, and a changed warning kept the old tooltip. Walking the keys
     * cannot fall behind the contract, and every field on it is a primitive, so
     * a shallow compare is the whole answer.</p>
     */
    private static sameOption(a: SelectOption, b: SelectOption): boolean {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const key of keys) {
            if ((a as unknown as Record<string, unknown>)[key]
                    !== (b as unknown as Record<string, unknown>)[key]) {
                return false;
            }
        }
        return true;
    }

    /** Matches label, hint and badge, so "maths" and "EMP014" both narrow the list. */
    search = (term: string, option: SelectOption): boolean =>
        searchTextOf(option).includes((term ?? '').trim().toLowerCase());

    writeValue(value: unknown): void {
        this.value = value ?? (this.multiple ? [] : null);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onModelChange(value: unknown): void {
        this.value = value;
        this.onChange(value);
        this.valueChange.emit(value);
    }

    onBlur(): void {
        this.onTouched();
    }
}
